// Weekly flashcard leaderboard: how many cards each player solved this week.
//
// Firestore fields on users/{uid}:
//   weekly_YYYYMMDD  cards solved in the week that started that Monday (UTC)
//   pendingReward    { weekId, rank, gems }  written by the weekly Cloud
//                    Function, claimed (and deleted) by the client
// Each week has its own field, so the board is just "order by this week's
// field": no composite index to create, and nothing to reset. A new week
// starts every Monday 00:00 UTC and its field starts out empty.

import { getAuth, onAuthStateChanged } from 'firebase/auth';
import {
  collection,
  deleteField,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  runTransaction,
  updateDoc,
  where,
} from 'firebase/firestore';
import { toast } from 'react-toastify';

import { db } from '../firebase';
import { awardScaleGem, onCoin } from './idleStore';

const DAY_MS = 86400000;
const WEEK_MS = 7 * DAY_MS;
export const BOARD_SIZE = 20;

/* ---------- the week ---------- */

/** Start of the current week: Monday 00:00 UTC, in ms. */
export const weekStart = (now = Date.now()) => {
  const d = new Date(now);
  const sinceMonday = (d.getUTCDay() + 6) % 7;
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - sinceMonday);
};

export const weekIdOf = (now = Date.now()) =>
  new Date(weekStart(now)).toISOString().slice(0, 10).replace(/-/g, '');

/** The user-doc field holding the count for a week. */
export const weekField = (weekId = weekIdOf()) => `weekly_${weekId}`;

/** When the board resets next, in ms. */
export const nextReset = (now = Date.now()) => weekStart(now) + WEEK_MS;

/* ---------- prizes (gems) ---------- */

export const PRIZES = [
  { from: 1, to: 1, gems: 100 },
  { from: 2, to: 2, gems: 80 },
  { from: 3, to: 3, gems: 50 },
  { from: 4, to: 10, gems: 30 },
  { from: 11, to: 20, gems: 25 },
];

export const rewardForRank = (rank) => {
  const tier = PRIZES.find((p) => rank >= p.from && rank <= p.to);
  return tier ? tier.gems : 0;
};

/* ---------- counting solved cards on this device ---------- */

const KEY = 'brassflows_weekly_v1';

const load = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(KEY));
    if (saved && saved.weekId === weekIdOf()) return saved;
  } catch (e) {
    // fall through to a fresh week
  }
  return { weekId: weekIdOf(), pending: 0 };
};

const save = (state) => {
  try {
    localStorage.setItem(KEY, JSON.stringify(state));
  } catch (e) {
    // storage unavailable; the count just won't survive a reload
  }
};

let flushTimer = null;
let flushing = false;

/** Sends the not-yet-uploaded solved cards to the player's account. */
export const flushWeekly = async () => {
  const user = getAuth().currentUser;
  const state = load();
  if (!user || state.pending <= 0 || flushing) return;

  flushing = true;
  const delta = state.pending;
  save({ ...state, pending: 0 });
  try {
    await runTransaction(db, async (tx) => {
      const ref = doc(db, 'users', user.uid);
      const snap = await tx.get(ref);
      const data = snap.data() || {};
      const field = weekField(state.weekId);
      tx.update(ref, { [field]: Number(data[field] || 0) + delta });
    });
  } catch (error) {
    // keep the cards so the next flush retries them
    const current = load();
    save({ ...current, pending: current.pending + delta });
  } finally {
    flushing = false;
  }
};

const scheduleFlush = () => {
  clearTimeout(flushTimer);
  flushTimer = setTimeout(flushWeekly, 4000);
};

const recordSolved = () => {
  const state = load();
  save({ ...state, pending: state.pending + 1 });
  scheduleFlush();
};

/** Last week's prize, if the weekly job left one: pays the gems once. */
export const claimPendingReward = async (user) => {
  try {
    const ref = doc(db, 'users', user.uid);
    const snap = await getDoc(ref);
    const reward = snap.data()?.pendingReward;
    if (!reward || !reward.gems) return;
    await updateDoc(ref, { pendingReward: deleteField() }); // first, so it can't pay twice
    awardScaleGem(reward.gems);
    toast(`You finished #${reward.rank} on last week's leaderboard: +${reward.gems} gems!`, {
      autoClose: 8000,
    });
  } catch (error) {
    console.error('Could not claim the weekly reward', error);
  }
};

/**
 * Starts counting solved flashcards (every right answer fires a coin event)
 * and uploading them. Call once from something that is always on screen.
 * Returns a cleanup function.
 */
export const startWeeklyTracking = () => {
  const stopCoins = onCoin(recordSolved);
  const stopAuth = onAuthStateChanged(getAuth(), (user) => {
    if (!user) return;
    flushWeekly();
    claimPendingReward(user);
  });
  const onHide = () => {
    if (document.visibilityState === 'hidden') flushWeekly();
  };
  document.addEventListener('visibilitychange', onHide);
  window.addEventListener('pagehide', flushWeekly);
  return () => {
    stopCoins();
    stopAuth();
    document.removeEventListener('visibilitychange', onHide);
    window.removeEventListener('pagehide', flushWeekly);
    clearTimeout(flushTimer);
  };
};

/* ---------- reading the board ---------- */

const toRow = (id, data, rank) => ({
  id,
  rank,
  username: data.username || 'Anonymous',
  solved: Number(data[weekField()] || 0),
  premium: data.membershipStatus === 'Premium',
});

/**
 * The top 20 for this week, plus the signed-in player's own row (with their
 * real rank) when they are not in it.
 */
export const fetchBoard = async () => {
  await flushWeekly(); // so your own latest cards are on the board
  const field = weekField();
  const usersRef = collection(db, 'users');

  const snap = await getDocs(query(usersRef, orderBy(field, 'desc'), limit(BOARD_SIZE)));
  const top = snap.docs.map((d, i) => toRow(d.id, d.data(), i + 1));

  const user = getAuth().currentUser;
  if (!user) return { top, me: null, signedIn: false };

  const inTop = top.find((row) => row.id === user.uid);
  if (inTop) return { top, me: inTop, signedIn: true };

  const mine = await getDoc(doc(db, 'users', user.uid));
  const data = mine.data() || {};
  const solved = Number(data[field] || 0);
  let rank = null;
  if (solved > 0) {
    const ahead = await getCountFromServer(query(usersRef, where(field, '>', solved)));
    rank = ahead.data().count + 1;
  }
  return { top, me: { ...toRow(user.uid, data, rank), solved }, signedIn: true };
};